import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SectionCard } from "@/components/Bits";
import { UploadButton } from "@/components/Upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, AlertTriangle, Search, Trash2, Paperclip, Eye, PlusCircle, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const CATS = ["General", "HR Policy", "IT", "Facilities", "Event", "Urgent"];
const PRIOS = ["Low", "Medium", "High", "Urgent"];
const prioColor = {
  Urgent: "bg-destructive/10 text-destructive border-destructive/20",
  High: "bg-warning/10 text-warning border-warning/30",
  Medium: "bg-primary/10 text-primary border-primary/20",
  Low: "bg-muted text-muted-foreground border-border",
};
const EMPTY = { title: "", content: "", category: "General", priority: "Medium", audience_type: "All Employees", audience_values: [], expiry_date: "", attachment_file_id: null, status: "published" };

export default function Announcements() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [reads, setReads] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (cat !== "all") p.set("category", cat);
    api.get(`/announcements?${p}`).then((r) => setList(r.data)).catch(() => {});
  }, [q, cat]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const save = async () => {
    try {
      if (form.id) await api.put(`/announcements/${form.id}`, form);
      else await api.post("/announcements", form);
      toast.success("Announcement saved");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const markRead = async (a) => { if (!a.is_read) { await api.post(`/announcements/${a.id}/read`); load(); } };
  const del = async (id) => { if (window.confirm("Delete this announcement?")) { await api.delete(`/announcements/${id}`); load(); } };
  const viewReads = async (id) => { const { data } = await api.get(`/announcements/${id}/reads`); setReads(data); };

  const urgent = list.filter((a) => a.priority === "Urgent" && !a.is_read);

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Company" title="Announcements">
        {isAdmin && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
            <DialogTrigger asChild><Button data-testid="new-announcement-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> New Announcement</Button></DialogTrigger>
            <DialogContent className="bg-popover max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{form.id ? "Edit" : "New"} Announcement</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="ann-title" /></div>
                <div><Label>Content</Label><Textarea rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1.5" data-testid="ann-content" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1.5" data-testid="ann-category"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger className="mt-1.5" data-testid="ann-priority"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover">{PRIOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="mt-1.5" data-testid="ann-status"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover"><SelectItem value="draft">Draft</SelectItem><SelectItem value="scheduled">Scheduled</SelectItem><SelectItem value="published">Published</SelectItem></SelectContent></Select></div>
                  <div><Label>Expiry Date</Label><Input type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1.5" data-testid="ann-expiry" /></div>
                </div>
                <div className="flex items-center justify-between">
                  <UploadButton label={form.attachment_file_id ? "Attached" : "Attach file"} done={!!form.attachment_file_id} onUploaded={(f) => setForm({ ...form, attachment_file_id: f.id })} />
                </div>
              </div>
              <DialogFooter><Button onClick={save} disabled={!form.title} data-testid="ann-save">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageTitle>

      {urgent.map((a) => (
        <div key={a.id} className="mb-4 flex items-start gap-3 p-4 rounded-md bg-destructive/10 border border-destructive/30" data-testid={`urgent-banner-${a.id}`}>
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1"><p className="font-heading font-bold text-destructive">{a.title}</p><p className="text-sm text-foreground mt-0.5">{a.content}</p></div>
          <Button size="sm" variant="outline" onClick={() => markRead(a)}>Dismiss</Button>
        </div>
      ))}

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search announcements…" className="pl-9" data-testid="ann-search" /></div>
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-44" data-testid="ann-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover"><SelectItem value="all">All Categories</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="space-y-3">
        {list.length === 0 && <p className="text-muted-foreground text-center py-12">No announcements.</p>}
        {list.map((a) => (
          <Card key={a.id} className={cn("p-5 rounded-md border-border cursor-pointer transition-colors", !a.is_read && "border-l-4 border-l-primary")} onClick={() => markRead(a)} data-testid={`ann-${a.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0"><Megaphone className="h-5 w-5 text-primary" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-bold">{a.title}</h3>
                  <span className={cn("px-2 py-0.5 rounded text-xs font-medium border", prioColor[a.priority])}>{a.priority}</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground border border-border">{a.category}</span>
                  {a.status !== "published" && <span className="px-2 py-0.5 rounded text-xs bg-warning/10 text-warning border border-warning/30 capitalize">{a.status}</span>}
                </div>
                <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">{a.content}</p>
                <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                  <span>{fmtDate(a.created_at)} · by {a.author}</span>
                  {a.attachment_file_id && <a href={fileUrl(a.attachment_file_id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-primary"><Paperclip className="h-3 w-3" /> Attachment</a>}
                  {isAdmin && <button onClick={(e) => { e.stopPropagation(); viewReads(a.id); }} className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`ann-reads-${a.id}`}><Eye className="h-3 w-3" /> {a.read_count} read</button>}
                  {isAdmin && <button onClick={(e) => { e.stopPropagation(); setForm({ ...a, expiry_date: a.expiry_date || "" }); setOpen(true); }} className="hover:text-foreground"><Pencil className="h-3 w-3" /></button>}
                  {isAdmin && <button onClick={(e) => { e.stopPropagation(); del(a.id); }} className="hover:text-destructive" data-testid={`ann-del-${a.id}`}><Trash2 className="h-3 w-3" /></button>}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!reads} onOpenChange={(o) => !o && setReads(null)}>
        <DialogContent className="bg-popover max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Read Receipts</DialogTitle></DialogHeader>
          {reads && <p className="text-sm"><span className="font-heading font-extrabold text-3xl font-mono-num">{reads.read_count}</span> <span className="text-muted-foreground">/ {reads.total} employees have read this.</span></p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
